import { useMutation, useQuery } from '@apollo/client'
import { StringHelpers } from '@dai/common'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'

import {
  ACTIVE_SUBSCRIPTION,
  ActiveSubscriptionQuery,
  ActiveSubscriptionQueryVariables,
  CANCEL_SUBSCRIPTION,
  CancelSubscriptionMutation,
  CancelSubscriptionMutationVariables,
  CREATE_UPDATE_PAYMENT_METHOD_PORTAL_SESSION,
  CreateUpdatePaymentMethodPortalSessionMutation,
  CreateUpdatePaymentMethodPortalSessionMutationVariables,
  PlanType,
  RENEW_SUBSCRIPTION,
  RenewSubscriptionMutation,
  RenewSubscriptionMutationVariables,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'
import { SubscriptionHelpers } from '@/utils/subscription.helpers'

const PlanManagementAndBilling = () => {
  const navigate = useNavigate()
  const { t } = useT()
  const { user } = useLoggedInUser()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  const {
    data,
    loading: queryLoading,
    updateQuery,
  } = useQuery<ActiveSubscriptionQuery, ActiveSubscriptionQueryVariables>(
    ACTIVE_SUBSCRIPTION,
    {
      fetchPolicy: 'network-only',
    },
  )

  const [cancelSubscription] = useMutation<
    CancelSubscriptionMutation,
    CancelSubscriptionMutationVariables
  >(CANCEL_SUBSCRIPTION)

  const [createUpdatePaymentMethodPortalSession] = useMutation<
    CreateUpdatePaymentMethodPortalSessionMutation,
    CreateUpdatePaymentMethodPortalSessionMutationVariables
  >(CREATE_UPDATE_PAYMENT_METHOD_PORTAL_SESSION)

  const [renewSubscription, { loading: renewSubscriptionLoading }] =
    useMutation<RenewSubscriptionMutation, RenewSubscriptionMutationVariables>(
      RENEW_SUBSCRIPTION,
      { notifyOnNetworkStatusChange: true },
    )

  const handleCancelSubscription = async () => {
    setLoading(true)
    setError(null)
    try {
      if (!data?.activeSubscription?.id) {
        throw new Error('No active subscription found')
      }
      await cancelSubscription({
        variables: {
          subscriptionId: data.activeSubscription.id,
        },
      })

      updateQuery(prev => {
        if (!prev.activeSubscription) return prev
        return {
          ...prev,
          activeSubscription: {
            ...prev.activeSubscription,
            cancelAt: prev.activeSubscription.billingPeriod?.endDate,
          },
        }
      })

      setShowCancelDialog(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleRenewSubscription = async () => {
    setLoading(true)
    setError(null)
    try {
      if (!data?.activeSubscription?.id) {
        throw new Error('No active subscription found')
      }
      await renewSubscription({
        variables: {
          subscriptionId: data?.activeSubscription?.id,
        },
      })

      updateQuery(prev => {
        if (!prev.activeSubscription) return prev
        return {
          ...prev,
          activeSubscription: {
            ...prev.activeSubscription,
            cancelAt: null,
          },
        }
      })

      setShowCancelDialog(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleManagePaymentMethods = async () => {
    try {
      const { data } = await createUpdatePaymentMethodPortalSession()
      if (data?.createUpdatePaymentMethodPortalSession?.url) {
        window.location.href = data.createUpdatePaymentMethodPortalSession.url
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    }
  }

  const handleSwitchPlan = () => {
    navigate('/pricing', { state: { from: '/admin/plan-management' } })
  }

  const handleManageStores = () => {
    navigate('/stores')
  }

  if (queryLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    )
  }

  const isFreePlan =
    data?.activeSubscription?.planType === PlanType.PREMIUM_FREE

  const paymentMethodInfo = data?.activeSubscription?.paymentMethod
  const baseMonthlySubtotal =
    (data?.activeSubscription?.centsPerUnit || 0) *
    (data?.activeSubscription?.unitQuantity || 0)
  const monthlySubtotal = data?.activeSubscription?.monthlySubtotal ?? 0
  const effectiveCentsPerUnit =
    data?.activeSubscription?.unitQuantity && monthlySubtotal > 0
      ? Math.round(monthlySubtotal / data.activeSubscription.unitQuantity)
      : data?.activeSubscription?.centsPerUnit ?? 0
  const hasActiveDiscount =
    !isFreePlan && monthlySubtotal > 0 && monthlySubtotal < baseMonthlySubtotal

  const renderPaymentMethodChip = () => {
    if (paymentMethodInfo?.usingLink) {
      return (
        <Tooltip title={t('billing.linkPaymentTooltip')}>
          <Chip
            label={
              <img
                src="/static/img/logos/link-logo.webp"
                alt="Link"
                style={{ height: '20px', verticalAlign: 'middle' }}
              />
            }
            sx={{
              ml: 4,
              height: 24,
              width: 'fit-content',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
            }}
          />
        </Tooltip>
      )
    } else if (paymentMethodInfo?.card?.last4) {
      return (
        <Chip
          sx={{
            ml: 4,
            width: 'fit-content',
            borderRadius: 10,
          }}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <Typography
                variant="body2"
                sx={{ lineHeight: '1', display: 'inline-block' }}
              >
                {paymentMethodInfo.card.brand} *{paymentMethodInfo.card.last4}
              </Typography>
            </Box>
          }
        />
      )
    }
    return null
  }

  return (
    <>
      <Helmet title={t('planManagement.title')} />
      <Stack spacing={4} paddingBottom={4}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h1">{t('planManagement.title')}</Typography>
          <Chip
            label={SubscriptionHelpers.getPlanTypeString(
              t,
              user?.activeSubscription,
            )}
            color="primary"
            sx={{
              width: 'fit-content',
              borderRadius: 10,
            }}
          />
        </Stack>
        {data?.activeSubscription?.trialEnd ? (
          <Alert severity="info">
            {t('planManagement.trialNotification', {
              trialEnd: new Date(
                data.activeSubscription.trialEnd,
              ).toLocaleDateString(),
            })}
          </Alert>
        ) : (
          !!data?.activeSubscription?.nextPlanType && (
            <Alert severity="info">
              {t('planManagement.downgradeNotification', {
                nextPlanType: SubscriptionHelpers.getPlanTypeString(
                  t,
                  data.activeSubscription.nextPlanType,
                ),
                date: new Date(data.activeSubscription.billingPeriod?.endDate),
              })}
            </Alert>
          )
        )}
        {!!data?.activeSubscription?.cancelAt && (
          <Alert severity="info">
            {t('planManagement.renewSubscriptionDescription', {
              date: new Date(data.activeSubscription.cancelAt),
              isTrial: !!data.activeSubscription.trialEnd,
            })}
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Divider />

        <Card>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h3">
                {t('planManagement.manageYourPlan')}
              </Typography>
              <Typography variant="h5">
                {t('planManagement.currentPlan', {
                  plan: SubscriptionHelpers.getPlanTypeString(
                    t,
                    user?.activeSubscription,
                  ),
                })}
              </Typography>

              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {t('planManagement.plan.activeStores')}
                      </TableCell>
                      <TableCell align="center">
                        {data?.activeSubscription?.unitQuantity}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t('planManagement.plan.pricePerStore')}
                        {hasActiveDiscount ? (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            ({t('planManagement.plan.discount')})
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">
                        {StringHelpers.formatCurrency(effectiveCentsPerUnit)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {t('planManagementAndBilling.plan.totalPerMonth')}
                        {isFreePlan
                          ? null
                          : data?.activeSubscription?.trialEnd
                            ? t('planManagementAndBilling.plan.payStarting')
                            : t('planManagementAndBilling.plan.paymentDate', {
                                date: new Date(
                                  data?.activeSubscription?.billingPeriod?.endDate,
                                ).toLocaleDateString(),
                              })}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">
                        {StringHelpers.formatCurrency(
                          isFreePlan ? 0 : monthlySubtotal,
                        )}{' '}
                        {t('planManagementAndBilling.nextCharge.plusTax')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
            <Box
              sx={{
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                gap: 3,
              }}
            >
              <Button
                sx={{ minWidth: 200 }}
                variant="outlined"
                onClick={handleManageStores}
                disabled={loading}
              >
                {t('planManagementAndBilling.stores.button.updateStores')}
              </Button>
              <Button
                sx={{ minWidth: 200 }}
                variant="contained"
                onClick={handleSwitchPlan}
              >
                {t('planManagement.button.switchPlan')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row">
                <Typography variant="h4">
                  {t('billing.paymentMethod')}
                </Typography>
                {renderPaymentMethodChip()}
              </Stack>
              <Typography variant="body1">
                {t('billing.paymentMethodDescription')}
              </Typography>
            </Stack>
            <Box
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <Button
                sx={{ minWidth: 200 }}
                variant="contained"
                onClick={() => {
                  handleManagePaymentMethods()
                }}
              >
                {t('billing.button.paymentMethod')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {!data?.activeSubscription?.cancelAt ? (
          <Card>
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h4">
                  {t('planManagement.cancelSubscription')}
                </Typography>
                <Typography variant="body1">
                  {t('planManagement.cancelSubscriptionDescription')}
                </Typography>
              </Stack>
              <Box
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <Button
                  sx={{ minWidth: 200 }}
                  variant="contained"
                  onClick={() => setShowCancelDialog(true)}
                >
                  {t('planManagement.button.cancelSubscription')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h4">
                  {t('planManagement.renewSubscription')}
                </Typography>
                <Typography variant="body1">
                  {t('planManagement.renewSubscriptionDescription', {
                    date: new Date(data.activeSubscription.cancelAt),
                    isTrial: !!data.activeSubscription.trialEnd,
                  })}
                </Typography>
              </Stack>
              <Box
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <Button
                  sx={{ minWidth: 200 }}
                  variant="contained"
                  onClick={handleRenewSubscription}
                  loading={renewSubscriptionLoading}
                >
                  {t('planManagement.button.renewSubscription')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Stack>
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
      >
        <DialogTitle>
          {t('planManagementAndBilling.cancelSubscriptionDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('planManagementAndBilling.cancelSubscriptionDialog.description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)} disabled={loading}>
            {t(
              'planManagementAndBilling.cancelSubscriptionDialog.keepSubscription',
            )}
          </Button>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            loading={loading}
          >
            {t(
              'planManagementAndBilling.cancelSubscriptionDialog.cancelSubscription',
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PlanManagementAndBilling
